/**
 * Líneas de cargo de liquidación (`courier_settlement_charge_lines`).
 * Soporta el formato actual (campos planos + `order`) y payloads legacy con `reconciliation`.
 */

export interface ChargeLineOrderNested {
  ref?: string | null;
  shop?: { name?: string | null; image_hash?: string | null } | null;
  status?: { id?: number | null; name?: string | null; description?: string | null } | null;
  shipment_status?: { id?: number | null; name?: string | null } | null;
  team?: { name?: string | null } | null;
}

export interface ChargeLineSourceAmounts {
  shipping_component?: number | null;
  cod_component?: number | null;
  extra_component?: number | null;
  total_carrier_line?: number | null;
  total_compare_to_logistics_ex_preparation?: number | null;
}

export interface ChargeLineReconciliationSource {
  warehouse_order_id_from_file?: number | null;
  amounts?: ChargeLineSourceAmounts | null;
  charged_weight_kg?: number | null;
}

export interface ChargeLineReconciliationDeltas {
  total_courier_vs_logistics_ex_preparation?: number | null;
  shipping_component_vs_SYSTEM_SHIPPING?: number | null;
  cod_component_vs_SYSTEM_COD?: number | null;
  weight_kg_courier_minus_system_courier_weight?: number | null;
}

export interface ChargeLineReconciliationSystem {
  order_id?: number | null;
  accounting_shipment_id_focus?: number | null;
  invoiced?: boolean | null;
  invoice_id?: number | null;
  weight_courier_kg?: number | null;
  accounting_line_totals_by_concept?: Record<string, number> | null;
  accounting_logistics_total_ex_PREPARATION?: number | null;
  deltas?: ChargeLineReconciliationDeltas | null;
}

export interface ChargeLineReconciliation {
  matched_order?: boolean | null;
  matched_accounting_shipment_resolved?: boolean | null;
  source?: ChargeLineReconciliationSource | null;
  system?: ChargeLineReconciliationSystem | null;
}

export interface CourierSettlementChargeLine {
  id: number;
  settlement_id?: number | null;
  /** API actual */
  source_order_id?: number | null;
  matched_accounting_shipment_id?: number | null;
  our_invoice_id?: number | null;
  our_weight_kg?: number | null;
  our_logistics_total?: number | null;
  our_shipment_type?: string | null;
  carrier_charged_shipping?: number | null;
  carrier_charged_cod?: number | null;
  carrier_charged_extra?: number | null;
  order?: ChargeLineOrderNested | null;
  /** Campos comunes */
  charged_weight_kg?: number | null;
  total_amount?: number | null;
  match_status?: string | null;
  matched_order_id?: number | null;
  /** Legacy */
  concept?: string | null;
  courier_line_kind?: string | null;
  amount_shipping?: number | null;
  amount_cod?: number | null;
  amount_extra?: number | null;
  source_internal_order_id?: number | null;
  reconciliation?: ChargeLineReconciliation | null;
}

export type ChargeLineBillingStatus = 'invoiced' | 'pending_invoice' | 'not_invoiced';

function num(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function bool(v: unknown): boolean | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'boolean') return v;
  return null;
}

function readRecord(v: unknown): Record<string, unknown> | null {
  if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
  return null;
}

function parseNamed(obj: Record<string, unknown> | null): { name?: string | null } | null {
  if (!obj) return null;
  return { name: obj.name != null ? String(obj.name) : null };
}

function parseShopNested(raw: unknown): ChargeLineOrderNested['shop'] {
  const s = readRecord(raw);
  if (!s) return null;
  return {
    name: s.name != null ? String(s.name) : null,
    image_hash: s.image_hash != null ? String(s.image_hash) : null,
  };
}

function parseOrderStatusNested(raw: unknown): ChargeLineOrderNested['status'] {
  const s = readRecord(raw);
  if (!s) return null;
  const id = num(s.id);
  return {
    id,
    name: s.name != null ? String(s.name) : null,
    description: s.description != null ? String(s.description) : null,
  };
}

function parseShipmentStatusNested(raw: unknown): ChargeLineOrderNested['shipment_status'] {
  const s = readRecord(raw);
  if (!s) return null;
  return {
    id: num(s.id),
    name: s.name != null ? String(s.name) : null,
  };
}

function parseOrderNested(raw: unknown): ChargeLineOrderNested | null {
  const o = readRecord(raw);
  if (!o) return null;
  return {
    ref: o.ref != null ? String(o.ref) : null,
    shop: parseShopNested(o.shop),
    status: parseOrderStatusNested(o.status),
    shipment_status: parseShipmentStatusNested(o.shipment_status),
    team: parseNamed(readRecord(o.team)),
  };
}

function parseReconciliationLegacy(recRaw: Record<string, unknown>): ChargeLineReconciliation | null {
  const srcRaw = readRecord(recRaw.source);
  const sysRaw = readRecord(recRaw.system);
  const deltasRaw = sysRaw ? readRecord(sysRaw.deltas) : null;
  const amountsRaw = srcRaw ? readRecord(srcRaw.amounts) : null;
  const conceptsRaw = sysRaw ? readRecord(sysRaw.accounting_line_totals_by_concept) : null;

  const accounting_line_totals_by_concept: Record<string, number> | null = conceptsRaw
    ? Object.fromEntries(
        Object.entries(conceptsRaw)
          .map(([k, val]) => [k, num(val)])
          .filter(([, v]) => v !== null) as [string, number][],
      )
    : null;

  return {
    matched_order: bool(recRaw.matched_order),
    matched_accounting_shipment_resolved: bool(recRaw.matched_accounting_shipment_resolved),
    source: srcRaw
      ? {
          warehouse_order_id_from_file: num(srcRaw.warehouse_order_id_from_file),
          charged_weight_kg: num(srcRaw.charged_weight_kg),
          amounts: amountsRaw
            ? {
                shipping_component: num(amountsRaw.shipping_component),
                cod_component: num(amountsRaw.cod_component),
                extra_component: num(amountsRaw.extra_component),
                total_carrier_line: num(amountsRaw.total_carrier_line),
                total_compare_to_logistics_ex_preparation: num(amountsRaw.total_compare_to_logistics_ex_preparation),
              }
            : null,
        }
      : null,
    system: sysRaw
      ? {
          order_id: num(sysRaw.order_id),
          accounting_shipment_id_focus: num(sysRaw.accounting_shipment_id_focus),
          invoiced: bool(sysRaw.invoiced),
          invoice_id: num(sysRaw.invoice_id),
          weight_courier_kg: num(sysRaw.weight_courier_kg),
          accounting_line_totals_by_concept:
            accounting_line_totals_by_concept && Object.keys(accounting_line_totals_by_concept).length > 0
              ? accounting_line_totals_by_concept
              : null,
          accounting_logistics_total_ex_PREPARATION: num(sysRaw.accounting_logistics_total_ex_PREPARATION),
          deltas: deltasRaw
            ? {
                total_courier_vs_logistics_ex_preparation: num(deltasRaw.total_courier_vs_logistics_ex_preparation),
                shipping_component_vs_SYSTEM_SHIPPING: num(deltasRaw.shipping_component_vs_SYSTEM_SHIPPING),
                cod_component_vs_SYSTEM_COD: num(deltasRaw.cod_component_vs_SYSTEM_COD),
                weight_kg_courier_minus_system_courier_weight: num(
                  deltasRaw.weight_kg_courier_minus_system_courier_weight,
                ),
              }
            : null,
        }
      : null,
  };
}

/** Parsea una fila del endpoint `/courier-settlements/:id/lines`. */
export function parseCourierSettlementChargeLine(raw: unknown): CourierSettlementChargeLine | null {
  const r = readRecord(raw);
  if (!r || r.id === undefined) return null;

  const recRaw = readRecord(r.reconciliation);
  const reconciliation = recRaw ? parseReconciliationLegacy(recRaw) : null;

  const orderParsed = parseOrderNested(r.order);

  return {
    id: Number(r.id),
    settlement_id: num(r.settlement_id),
    source_order_id: num(r.source_order_id),
    matched_accounting_shipment_id: num(r.matched_accounting_shipment_id),
    our_invoice_id: num(r.our_invoice_id),
    our_weight_kg: num(r.our_weight_kg),
    our_logistics_total: num(r.our_logistics_total),
    our_shipment_type: r.our_shipment_type != null ? String(r.our_shipment_type) : null,
    carrier_charged_shipping: num(r.carrier_charged_shipping),
    carrier_charged_cod: num(r.carrier_charged_cod),
    carrier_charged_extra: num(r.carrier_charged_extra),
    order: orderParsed,
    charged_weight_kg: num(r.charged_weight_kg),
    total_amount: num(r.total_amount),
    match_status: r.match_status != null ? String(r.match_status) : null,
    matched_order_id: num(r.matched_order_id),
    concept: r.concept != null ? String(r.concept) : null,
    courier_line_kind: r.courier_line_kind != null ? String(r.courier_line_kind) : null,
    amount_shipping: num(r.amount_shipping),
    amount_cod: num(r.amount_cod),
    amount_extra: num(r.amount_extra),
    source_internal_order_id: num(r.source_internal_order_id),
    reconciliation,
  };
}

/** Id de pedido útil para búsqueda / enlaces. */
export function getChargeLineOrderId(line: CourierSettlementChargeLine): number | null {
  const candidates = [
    line.source_order_id,
    line.matched_order_id,
    line.source_internal_order_id,
    line.reconciliation?.source?.warehouse_order_id_from_file,
    line.reconciliation?.system?.order_id,
  ];
  for (const c of candidates) {
    if (c != null && Number(c) > 0) return Number(c);
  }
  return null;
}

function hasPositiveId(v: number | null | undefined): boolean {
  return v != null && Number(v) > 0;
}

/**
 * - Envío contable + factura nuestra → facturado
 * - Envío contable sin factura → por facturar
 * - Sin envío contable ni factura → no facturado
 */
export function getChargeLineBillingStatus(line: CourierSettlementChargeLine): ChargeLineBillingStatus {
  const shipmentId =
    line.matched_accounting_shipment_id ?? line.reconciliation?.system?.accounting_shipment_id_focus ?? null;
  const invoiceId = line.our_invoice_id ?? line.reconciliation?.system?.invoice_id ?? null;

  const hasShipment = hasPositiveId(shipmentId);
  const hasInvoice = hasPositiveId(invoiceId);

  if (hasShipment && hasInvoice) return 'invoiced';
  if (hasShipment && !hasInvoice) return 'pending_invoice';
  if (!hasShipment && !hasInvoice) return 'not_invoiced';
  return 'invoiced';
}
